import {
  type UnionAddress,
  joinAddress,
  toUnionAddress,
} from "@anaconda/core/blockchain/address"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { User } from "@privy-io/react-auth"

type LinkedAccount = NonNullable<User["linkedAccounts"]>[number]

function isEmbeddedPrivyWallet(account: LinkedAccount): boolean {
  if ("walletClientType" in account) {
    const walletClientType = account.walletClientType
    if (walletClientType === "privy" || walletClientType === "privy-v2") {
      return true
    }
  }

  if ("connectorType" in account && account.connectorType === "embedded") {
    return true
  }

  return false
}

export function hasExternalLinkedSolanaWallet(
  user: User | null | undefined,
): boolean {
  return (
    user?.linkedAccounts?.some(
      (account) =>
        isSolanaLinkedAccount(account) && !isEmbeddedPrivyWallet(account),
    ) ?? false
  )
}

export function hasExternalLinkedEvmWallet(
  user: User | null | undefined,
): boolean {
  return (
    user?.linkedAccounts?.some(
      (account) =>
        isEvmLinkedAccount(account) && !isEmbeddedPrivyWallet(account),
    ) ?? false
  )
}

/** Wallet-login Solana user without email or external EVM. */
export function isSolanaPrimaryUser(user: User | null | undefined): boolean {
  if (!hasExternalLinkedSolanaWallet(user)) {
    return false
  }

  if (hasExternalLinkedEvmWallet(user)) {
    return false
  }

  const hasEmailAccount = user?.linkedAccounts?.some(
    (account) => account.type === "email",
  )

  return !hasEmailAccount
}

/** Popup header: Solana when user linked external Solana without external EVM. */
export function resolveProfileDisplayUnionAddress(
  evmUnion: UnionAddress | undefined,
  solanaUnion: UnionAddress | undefined,
  user: User | null | undefined,
): UnionAddress | undefined {
  if (isSolanaPrimaryUser(user) && solanaUnion) {
    return solanaUnion
  }

  if (
    hasExternalLinkedSolanaWallet(user) &&
    !hasExternalLinkedEvmWallet(user) &&
    solanaUnion
  ) {
    return solanaUnion
  }

  return evmUnion ?? solanaUnion
}

/** Show "Add Solana" when EVM is present but no external/connected Solana wallet. */
export function shouldOfferSolanaWalletLink(options: {
  hasEvmWallet: boolean
  user?: User | null
  connectedSolanaAddress?: string
}): boolean {
  if (!options.hasEvmWallet) {
    return false
  }

  if (
    resolveVerifiedConnectedSolanaAddress(
      options.user,
      options.connectedSolanaAddress,
    )
  ) {
    return false
  }

  return !hasExternalLinkedSolanaWallet(options.user)
}

/** Show "Connect EVM" when only a Solana wallet is linked. */
export function shouldOfferEvmWalletLink(options: {
  hasSolanaWallet: boolean
  hasEvmWallet: boolean
}): boolean {
  return options.hasSolanaWallet && !options.hasEvmWallet
}

export type EvmSignerState = "ready" | "missing" | "mismatch"

/**
 * Whether the Profile EVM address can sign right now. Profile identity comes
 * from Privy's linked accounts, so it survives a locked or disconnected wallet
 * that wagmi no longer exposes as a signer.
 */
export function getEvmSignerState(options: {
  profileAddress: string | undefined
  signerAddress: string | undefined
}): EvmSignerState {
  // Without a Profile address there is no account to compare against yet.
  if (!options.signerAddress || !options.profileAddress) {
    return "missing"
  }

  return options.signerAddress.toLowerCase() ===
    options.profileAddress.toLowerCase()
    ? "ready"
    : "mismatch"
}

function isEvmLinkedAccount(account: LinkedAccount): boolean {
  if (
    account.type !== "wallet" &&
    account.type !== "smart_wallet" &&
    account.type !== "farcaster"
  ) {
    return false
  }

  if (!("address" in account) || !account.address) {
    return false
  }

  if ("chainType" in account && account.chainType === "solana") {
    return false
  }

  if ("chainType" in account && account.chainType === "ethereum") {
    return true
  }

  return account.address.startsWith("0x")
}

function isSolanaLinkedAccount(account: LinkedAccount): boolean {
  if (account.type !== "wallet" && account.type !== "smart_wallet") {
    return false
  }

  if (!("address" in account) || !account.address) {
    return false
  }

  return "chainType" in account && account.chainType === "solana"
}

function isLinkedEvmAddress(
  user: User | null | undefined,
  address: string | undefined,
): boolean {
  if (!address?.trim() || !user) {
    return false
  }

  const normalized = address.toLowerCase()

  return (
    user.linkedAccounts?.some(
      (account) =>
        isEvmLinkedAccount(account) &&
        "address" in account &&
        account.address.toLowerCase() === normalized,
    ) ?? false
  )
}

function isLinkedSolanaAddress(
  user: User | null | undefined,
  address: string | undefined,
): boolean {
  if (!address?.trim() || !user) {
    return false
  }

  const normalized = address.trim()

  return (
    user.linkedAccounts?.some(
      (account) =>
        isSolanaLinkedAccount(account) &&
        "address" in account &&
        account.address === normalized,
    ) ?? false
  )
}

function resolveVerifiedConnectedEvmAddress(
  user: User | null | undefined,
  address: string | undefined,
): string | undefined {
  if (!isLinkedEvmAddress(user, address)) {
    return undefined
  }

  return address?.toLowerCase()
}

function resolveVerifiedConnectedSolanaAddress(
  user: User | null | undefined,
  address: string | undefined,
): string | undefined {
  if (!isLinkedSolanaAddress(user, address)) {
    return undefined
  }

  return address?.trim()
}

export function resolveLinkedExternalEvmAddress(
  user: User | null | undefined,
): string | undefined {
  const linkedAccount = user?.linkedAccounts?.find(
    (account) => isEvmLinkedAccount(account) && !isEmbeddedPrivyWallet(account),
  )

  if (!linkedAccount || !("address" in linkedAccount)) {
    return undefined
  }

  return linkedAccount.address.toLowerCase()
}

export function resolveLinkedEvmAddress(
  user: User | null | undefined,
): string | undefined {
  const linkedAccount = user?.linkedAccounts?.find(isEvmLinkedAccount)

  if (!linkedAccount || !("address" in linkedAccount)) {
    return undefined
  }

  return linkedAccount.address.toLowerCase()
}

export function resolveLinkedExternalSolanaAddress(
  user: User | null | undefined,
): string | undefined {
  const linkedAccount = user?.linkedAccounts?.find(
    (account) =>
      isSolanaLinkedAccount(account) && !isEmbeddedPrivyWallet(account),
  )

  if (!linkedAccount || !("address" in linkedAccount)) {
    return undefined
  }

  return linkedAccount.address
}

export function resolveLinkedSolanaAddress(
  user: User | null | undefined,
): string | undefined {
  const linkedAccount = user?.linkedAccounts?.find(isSolanaLinkedAccount)

  if (!linkedAccount || !("address" in linkedAccount)) {
    return undefined
  }

  return linkedAccount.address
}

export function resolveEvmWalletAddress(options: {
  user?: User | null
  globalWalletAddress?: string
  walletClientAddress?: string
  connectedWalletAddress?: string
}): string | undefined {
  const externalLinkedAddress = resolveLinkedExternalEvmAddress(options.user)
  if (externalLinkedAddress) {
    return externalLinkedAddress
  }

  if (isSolanaPrimaryUser(options.user)) {
    return (
      options.globalWalletAddress?.toLowerCase() ||
      resolveVerifiedConnectedEvmAddress(
        options.user,
        options.walletClientAddress,
      ) ||
      resolveVerifiedConnectedEvmAddress(
        options.user,
        options.connectedWalletAddress,
      )
    )
  }

  const linkedAddress = resolveLinkedEvmAddress(options.user)
  if (linkedAddress) {
    return linkedAddress
  }

  if (options.globalWalletAddress) {
    return options.globalWalletAddress.toLowerCase()
  }

  const verifiedWalletClientAddress = resolveVerifiedConnectedEvmAddress(
    options.user,
    options.walletClientAddress,
  )
  if (verifiedWalletClientAddress) {
    return verifiedWalletClientAddress
  }

  const verifiedConnectedWalletAddress = resolveVerifiedConnectedEvmAddress(
    options.user,
    options.connectedWalletAddress,
  )
  if (verifiedConnectedWalletAddress) {
    return verifiedConnectedWalletAddress
  }

  return undefined
}

export function resolveSolanaWalletAddress(options: {
  user?: User | null
  connectedSolanaAddress?: string
}): string | undefined {
  const externalLinkedAddress = resolveLinkedExternalSolanaAddress(options.user)
  if (externalLinkedAddress) {
    return externalLinkedAddress
  }

  const verifiedConnectedAddress = resolveVerifiedConnectedSolanaAddress(
    options.user,
    options.connectedSolanaAddress,
  )
  if (verifiedConnectedAddress) {
    return verifiedConnectedAddress
  }

  const linkedAddress = resolveLinkedSolanaAddress(options.user)
  if (linkedAddress) {
    return linkedAddress
  }

  return undefined
}

export function toEvmUnionAddress(
  evmAddress: string | undefined,
): UnionAddress | undefined {
  if (!evmAddress) {
    return undefined
  }

  return toUnionAddress(joinAddress(Blockchain.ETHEREUM, evmAddress))
}

export function toSolanaUnionAddress(
  solanaAddress: string | undefined,
): UnionAddress | undefined {
  if (!solanaAddress) {
    return undefined
  }

  return toUnionAddress(joinAddress(Blockchain.SOLANA, solanaAddress))
}

/** EVM-first aggregate for legacy single-wallet callers. */
export function resolvePrimaryUnionAddress(
  evmUnion?: UnionAddress,
  solanaUnion?: UnionAddress,
): UnionAddress | undefined {
  return evmUnion ?? solanaUnion
}

export function resolvePortfolioUnionAddress(
  evmUnion?: UnionAddress,
  solanaUnion?: UnionAddress,
): UnionAddress | undefined {
  return resolvePrimaryUnionAddress(evmUnion, solanaUnion)
}
